import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import { useAuth } from '../../contexts/AuthContext';
import BrandLogo from '../brand/BrandLogo';

interface NavItem {
  name: string;
  path: string;
  children?: NavItem[];
}

export function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useTranslation(['common']);
  const { user, signOut } = useAuth();
  const authState = { from: location };

  const showTransparent = false;

  const isPathActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const handleLogout = async () => {
    setMobileMenuOpen(false);
    navigate('/');
    await signOut();
  };

  const navItems: NavItem[] = [
    { name: t('common:migrated.accueil'), path: '/' },
    {
      name: t('common:migrated.comment_a_marche'),
      path: '/how-it-works',
      children: [
        { name: t('common:migrated.vos_engagements'), path: '/how-it-works/engagements' },
        { name: `${t('common:migrated.pays')} & ${t('common:migrated.logements')}`, path: '/how-it-works/countries' },
      ],
    },
    { name: t('common:migrated._change_h_te'), path: '/host-exchanges' },
    { name: t('common:migrated.devenir_membre'), path: '/become-host' }
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${showTransparent
      ? 'bg-transparent'
      : 'bg-white/95 backdrop-blur-sm shadow-sm'
      }`}>



      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group cursor-pointer">
            <BrandLogo
              compact
              withOvalBackdrop={showTransparent}
              imageClassName="transition-transform duration-300 group-hover:scale-[1.02]"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => {
              if (!item.children?.length) {
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${isPathActive(item.path)
                      ? 'bg-terra-500 text-white shadow-md'
                      : showTransparent
                        ? 'text-white hover:bg-white/10'
                        : 'text-gray-700 hover:bg-terra-50'
                      }`}
                  >
                    {item.name}
                  </Link>
                );
              }

              const itemActive = isPathActive(item.path);

              return (
                <div key={item.path} className="relative group">
                  <Link
                    to={item.path}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${itemActive
                      ? 'bg-terra-500 text-white shadow-md'
                      : showTransparent
                        ? 'text-white hover:bg-white/10'
                        : 'text-gray-700 hover:bg-terra-50'
                      }`}
                  >
                    {item.name}
                    <i className="ri-arrow-down-s-line text-base" />
                  </Link>
                  <div className="pointer-events-none absolute left-0 top-full z-50 pt-2 opacity-0 transition-all duration-200 group-hover:pointer-events-auto group-hover:opacity-100">
                    <div className="min-w-[220px] rounded-xl border border-gray-200 bg-white p-2 shadow-xl">
                      {item.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className={`block rounded-lg px-3 py-2 text-sm font-medium transition-colors ${isPathActive(child.path)
                            ? 'bg-terra-50 text-terra-700'
                            : 'text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right Section */}
          <div className="hidden lg:flex items-center gap-3">
            <LanguageSelector />
            {user ? (
              <>
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 rounded-lg border border-red-200 text-red-600 text-sm font-medium hover:bg-red-50 transition-all duration-200 cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
                >
                  <i className="ri-logout-box-r-line" />
                  {t('common:migrated.se_d_connecter')}
                </button>
                <Link
                  to="/dashboard"
                  className="px-5 py-2 bg-gradient-to-r from-terra-500 to-terra-600 text-white rounded-lg text-sm font-medium hover:shadow-lg transition-all duration-200 cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
                >
                  <i className="ri-dashboard-line" />
                  {t('common:migrated.mon_espace')}
                </Link>
              </>
            ) : (
              <Link
                to="/auth"
                state={authState}
                className="px-5 py-2 bg-gradient-to-r from-terra-500 to-terra-600 text-white rounded-lg text-sm font-medium hover:shadow-lg transition-all duration-200 cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
              >
                <i className="ri-user-line" />
                {t('common:migrated.connexion')}
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors cursor-pointer ${showTransparent
              ? 'text-white hover:bg-white/10'
              : 'text-gray-700 hover:bg-gray-100'
              }`}
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3 space-y-1">
            {navItems.map((item) => (
              <div key={item.path}>
                <Link
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors cursor-pointer ${isPathActive(item.path)
                    ? 'bg-terra-500 text-white'
                    : 'text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  {item.name}
                </Link>
                {item.children?.length ? (
                  <div className="ml-3 mt-1 space-y-1 border-l border-gray-200 pl-3">
                    {item.children.map((child) => (
                      <Link
                        key={child.path}
                        to={child.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`block rounded-lg px-3 py-2 text-sm transition-colors ${isPathActive(child.path)
                          ? 'bg-terra-50 text-terra-700 font-semibold'
                          : 'text-gray-600 hover:bg-gray-50'
                          }`}
                      >
                        {child.name}
                      </Link>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
            <div className="pt-3 border-t border-gray-100 space-y-2">
              <div className="px-4 py-2">
                <LanguageSelector />
              </div>
              {user ? (
                <>
                  <div className="flex items-center justify-between px-4">
                    <span className="text-sm font-medium text-emerald-700">{t('common:migrated.connect_')}</span>
                    <button
                      onClick={handleLogout}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <i className="ri-logout-box-r-line text-xl"></i>
                    </button>
                  </div>
                  <Link
                    to="/my-calendar"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block w-full px-4 py-3 bg-gradient-to-r from-terra-500 to-terra-600 text-white rounded-lg text-sm font-medium hover:shadow-lg transition-all duration-200 cursor-pointer whitespace-nowrap text-left"
                  >
                    {t('common:migrated.mon_espace')}
                  </Link>
                </>
              ) : (
                <Link
                  to="/auth"
                  state={authState}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block w-full px-4 py-3 bg-gradient-to-r from-terra-500 to-terra-600 text-white rounded-lg text-sm font-medium hover:shadow-lg transition-all duration-200 cursor-pointer whitespace-nowrap text-left"
                >
                  <i className="ri-user-line mr-2" />
                  {t('common:migrated.connexion')}
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;
